/**
 * Tipos da criação assistida de apreciação por IA.
 *
 * Espelham `backend/src/services/machine-context.service.ts` e
 * `backend/src/services/ia-job.service.ts`.
 */

export type NivelConfianca = 'alta' | 'media' | 'baixa' | 'ausente';

export type OrigemCampo =
  | 'meta.maquinaId'
  | 'meta.apreciacao'
  | 'cronogramaTarefa'
  | 'ativo.sismana'
  | 'ativo.tag'
  | 'ativo.nome-exato'
  | 'ativo.nome-fuzzy'
  | 'maquina.nome'
  | 'apreciacao-vizinha'
  | 'historico-area'
  | 'constante'
  | 'usuario'
  | 'inferido'
  | 'ausente';

export interface CampoResolvido<T> {
  valor: T | null;
  confianca: NivelConfianca;
  origem: OrigemCampo;
  detalhe?: string;
  alternativas?: Array<{ valor: T; rotulo: string; score?: number }>;
}

export interface Entrevistado {
  nome: string;
  cargo: string;
}

export interface EntrevistadoSugerido extends Entrevistado {
  ocorrencias: number;
  escopo: 'local' | 'area' | 'projeto';
}

export interface TemplateSugerido {
  id: string;
  nome: string;
  tipoMaquina: string | null;
  partes: string[];
}

export interface ApreciacaoVizinha {
  id: string;
  numero: string;
  nomeMaquina: string | null;
  similaridade: number;
}

/** Resultado do dry-run que alimenta o modal de pré-voo. */
export interface PreviewIA {
  projetoId: string;
  machineFolder: string;
  machineName: string;
  totalFotos: number;

  ativoId: CampoResolvido<string>;
  maquinaId: CampoResolvido<string>;
  tarefaId: CampoResolvido<string>;

  nomeMaquina: CampoResolvido<string>;
  tipoMaquina: CampoResolvido<string>;
  sismana: CampoResolvido<string>;
  tag: CampoResolvido<string>;
  identificacao: CampoResolvido<string>;

  area: CampoResolvido<string>;
  areaExibicao: CampoResolvido<string>;
  local: CampoResolvido<string>;

  entrevistado: CampoResolvido<Entrevistado>;
  entrevistadoAlternativas: EntrevistadoSugerido[];

  apreciacoesVizinhas: ApreciacaoVizinha[];
  templateSugerido: CampoResolvido<TemplateSugerido>;
  dataEntrevista: CampoResolvido<string>;

  confiancaGeral: NivelConfianca;
  requerConfirmacao: boolean;
  avisos: string[];

  fotos: number;
  descricoesEmCache: number;
  jobEmAndamento: { jobId: string; status: string } | null;
  apreciacoesVinculadas: string[];
}

/** Correções feitas pelo usuário no modal de pré-voo. */
export interface OverrideContextoIA {
  ativoId?: string | null;
  maquinaId?: string | null;
  nomeMaquina?: string;
  tipoMaquina?: string;
  sismana?: string;
  tag?: string;
  area?: string;
  local?: string;
  entrevistado?: Entrevistado;
  templateMultiParteId?: string | null;
  dataEntrevista?: string;
}

export type IaJobStatus =
  | 'pendente'
  | 'executando'
  | 'concluido'
  | 'falhou'
  | 'cancelado'
  | 'interrompido';

export type IaJobEtapaId =
  | 'resolver-contexto'
  | 'criar-apreciacao'
  | 'autofill-cabecalho'
  | 'carregar-fotos'
  | 'descrever-imagens'
  | 'selecionar-destaque'
  | 'decidir-partes'
  | 'detalhar-partes'
  | 'persistir-riscos'
  | 'finalizar';

export interface IaJobEtapa {
  id: IaJobEtapaId;
  rotulo: string;
  status: 'pendente' | 'executando' | 'concluida';
}

export interface IaJobAviso {
  etapa: IaJobEtapaId;
  mensagem: string;
}

export interface ProvenienciaCampo {
  origem: 'constante' | 'banco' | 'planilha' | 'ia' | 'usuario' | 'derivado';
  confianca: NivelConfianca;
  detalhe?: string;
}

export interface IaJob {
  jobId: string;
  tipo: 'apreciacao-ia';
  status: IaJobStatus;
  projetoId: string;
  machineFolder: string;
  usuarioId: string | null;
  apreciacaoId: string | null;

  etapaAtual: IaJobEtapaId;
  etapasConcluidas: IaJobEtapaId[];
  progresso: { atual: number; total: number; rotulo: string };
  etapas: IaJobEtapa[];
  percentual: number;

  cancelamentoSolicitado: boolean;
  iniciadoEm: string;
  atualizadoEm: string;
  finalizadoEm?: string | null;

  erro?: { etapa: IaJobEtapaId; mensagem: string } | null;
  avisos: IaJobAviso[];
  proveniencia?: Record<string, ProvenienciaCampo>;
}

/** Situação resumida por pasta, para os selos da grade. */
export interface StatusIaPorPasta {
  machineFolder: string;
  jobId: string | null;
  status: IaJobStatus | null;
  apreciacaoId: string | null;
  percentual: number | null;
  rotulo: string | null;
}

export const JOB_EM_ANDAMENTO: IaJobStatus[] = ['pendente', 'executando'];

export function jobEstaAtivo(status: IaJobStatus | null | undefined): boolean {
  return !!status && JOB_EM_ANDAMENTO.includes(status);
}
